import React from "react";
import ComponentLoading from "../../../modules/ComponentLoading";
import { Box, Flex, Icon, Link, Text, useTranslation } from "@familyzone/component-library";
import { colors } from "../../../theme";
import type { HubArticle } from "../../../types/Community";
import type { TopAppViewItem } from "../../../storez/CommunityDashboardStore";

interface TopAppsSectionProps {
  topApps: TopAppViewItem[];
  isLoading: boolean;
  isError: boolean;
  hasArticlesError: boolean;
}

function TopAppsSection({ topApps, isLoading, isError, hasArticlesError }: TopAppsSectionProps): JSX.Element {
  const { t } = useTranslation();
  const hasData = topApps.length > 0;

  return (
    <Box
      bg="neutrals.15"
      p="sp24"
      borderRadius="md"
      flex="1 1 270px"
      minWidth="270px"
      maxWidth={{
        base: "100%",
        lg: hasArticlesError ? "414px" : "40%",
      }}
      data-testid="parent-engagement-top-apps"
    >
      <Text fontFamily="heading" fontWeight="medium" fontSize="lg" color="text.title" mb="40">
        {t("Top 3 App Insights")}
      </Text>

      {isLoading ? (
        <Flex justify="center" align="center" height="150px" data-testid="parent-engagement-top-apps-loading">
          <ComponentLoading />
        </Flex>
      ) : isError ? (
        <Flex justify="center" align="center" height="150px" data-testid="parent-engagement-top-apps-loading">
          <Text fontSize="md" color="text.paragraph.light">
            {t("Error loading top apps")}
          </Text>
        </Flex>
      ) : !hasData ? (
        <Flex justify="center" align="center" height="150px" data-testid="parent-engagement-top-apps-loading">
          <Text fontSize="md" color="text.paragraph.light">
            {t("No data yet")}
          </Text>
        </Flex>
      ) : (
        <Flex direction="column" gap="sp16">
          {topApps?.map(({ signature, name, faviconUrl }: TopAppViewItem) => {
            return (
              <Box key={signature}>
                <Flex align="center" gap="sp12">
                  {faviconUrl ? (
                    <Box as="img" src={faviconUrl} alt={name} width="32px" height="32px" borderRadius="md" objectFit="contain" />
                  ) : (
                    <Box
                      bg="brand.300"
                      height="32px"
                      width="32px"
                      minWidth="32px"
                      borderRadius="md"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                    >
                      <Icon icon="fa-solid fa-mobile" color="brand.400" />
                    </Box>
                  )}
                  <Text fontWeight="medium" fontSize="xl" color="text.title">
                    {name}
                  </Text>
                </Flex>
              </Box>
            );
          })}
        </Flex>
      )}
    </Box>
  );
}

interface TopArticlesSectionProps {
  topArticles: HubArticle[];
  isLoading: boolean;
  isError: boolean;
  safetyHubUrl?: string;
}

function TopArticlesSection({ topArticles, isLoading, isError, safetyHubUrl }: TopArticlesSectionProps): JSX.Element | null {
  const { t } = useTranslation();

  if (isError || !safetyHubUrl || topArticles.length === 0) {
    return null;
  }

  return (
    <Box
      bg="neutrals.15"
      p="sp24"
      borderRadius="md"
      flex={{ base: "1 1 100%", lg: "1 1 360px" }}
      minWidth="360px"
      data-testid="parent-engagement-top-articles"
    >
      <Text fontFamily="heading" fontWeight="medium" fontSize="lg" color="text.title" mb="40">
        {t("Top 3 Hub Articles")}
      </Text>

      <Box overflowX="auto" width="100%" minWidth={0} borderRadius="md">
        {isLoading ? (
          <Flex justify="center" align="center" height="150px" data-testid="parent-engagement-top-articles-loading">
            <ComponentLoading />
          </Flex>
        ) : (
          <Flex direction="column" minWidth="min-content">
            {topArticles?.map((article: HubArticle, index: number) => (
              <Link key={index} href={article.url} target="_blank" role="group">
                <Flex align="center" justify="space-between" p="sp8" pt={index === 0 ? 0 : "sp8"} borderRadius="md" minWidth="300px">
                  <Flex align="center" gap="sp12">
                    <Box
                      bg="brand.300"
                      height="32px"
                      width="32px"
                      minWidth="32px"
                      flexShrink={0}
                      borderRadius="md"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                    >
                      <Icon icon="fa-solid fa-newspaper" color="brand.400" />
                    </Box>
                    <Text fontSize="md" color="text.title" fontWeight="medium">
                      {article.title}
                    </Text>
                  </Flex>
                  <Box marginLeft="sp12">
                    <Box
                      boxShadow={20}
                      bgColor="neutrals.0"
                      height="32px"
                      width="32px"
                      borderRadius="md"
                      position="relative"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      _groupHover={{ bgColor: "brand.400" }}
                    >
                      <Icon icon="fa-solid fa-arrow-right" color="text.paragraph.light" _groupHover={{ color: colors.neutrals["0"] }} />
                    </Box>
                  </Box>
                </Flex>
              </Link>
            ))}
          </Flex>
        )}
      </Box>

      <Flex justify="center" mt="sp24">
        <Link href={safetyHubUrl} target="_blank" color="brand.400" fontSize="sm" fontWeight="medium">
          {t("Go to Safety Hub")}
        </Link>
      </Flex>
    </Box>
  );
}

interface ParentEngagementProps {
  topApps: TopAppViewItem[];
  isLoadingTopApps: boolean;
  isErrorTopApps: boolean;
  topArticles: HubArticle[];
  isLoadingTopArticles: boolean;
  isErrorTopArticles: boolean;
  safetyHubUrl?: string;
}

export default function ParentEngagement({
  topApps,
  isLoadingTopApps,
  isErrorTopApps,
  topArticles,
  isLoadingTopArticles,
  isErrorTopArticles,
  safetyHubUrl,
}: ParentEngagementProps): JSX.Element | null {
  const { t } = useTranslation();

  if (topApps.length === 0 && !safetyHubUrl) {
    return null;
  }

  return (
    <Box
      bg="neutrals.0"
      p="sp24"
      borderRadius="md"
      boxShadow={30}
      width="100%"
      minWidth={0}
      overflow="hidden"
      data-testid="parent-engagement"
    >
      <Box mb="sp24">
        <Text fontFamily="heading" fontWeight="medium" fontSize="lg" color="text.title">
          {t("Parent Engagement")}
        </Text>
        <Text fontSize="md" color="text.title" mt="sp4">
          {t("These are the main articles your parent community is engaging with")}
        </Text>
      </Box>

      <Flex gap="sp24" minWidth={0} direction={{ base: "column", lg: "row" }}>
        <TopAppsSection topApps={topApps} isLoading={isLoadingTopApps} isError={isErrorTopApps} hasArticlesError={isErrorTopArticles} />
        <TopArticlesSection
          topArticles={topArticles}
          isLoading={isLoadingTopArticles}
          isError={isErrorTopArticles}
          safetyHubUrl={safetyHubUrl}
        />
      </Flex>
    </Box>
  );
}
